import React, { useState } from 'react';
import styled from 'styled-components';
import { FiMapPin, FiMail, FiFacebook, FiTwitter } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import contactService from '../services/contactService';

interface ContactPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onFaqClick?: () => void;
  onFeedbackClick?: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick: () => void;
  onMkAcademyInPersonClick: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  onLogout: () => void;
}

const ContactContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const ContactContent = styled.div`
  display: flex;
  min-height: calc(100vh - 140px);
`;

const ContactInfoSection = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #253C8C 0%, #1e40af 100%);
  padding: 4rem 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
`;

const ContactFormSection = styled.div`
  flex: 1;
  background: white;
  padding: 4rem 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 3rem;
  
  .icon-container {
    width: 50px;
    height: 50px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1.5rem;
    flex-shrink: 0;
  }
  
  .icon {
    color: #10b981;
    font-size: 1.5rem;
  }
  
  .content {
    flex: 1;
  }
  
  .label {
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: #e2e8f0;
  }
  
  .value {
    font-size: 1.1rem;
    font-weight: 600;
    color: white;
  }
`;

const FollowUsSection = styled.div`
  margin-top: 2rem;
  
  .title {
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: white;
    letter-spacing: 1px;
  }
  
  .social-icons {
    display: flex;
    gap: 1rem;
  }
  
  .social-icon {
    width: 45px;
    height: 45px;
    background: white;
    border: 2px solid #253C8C;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: #fbbf24;
      border-color: #fbbf24;
    }
    
    .icon {
      color: #253C8C;
      font-size: 1.2rem;
    }
  }
`;

const FormTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 3rem;
  text-align: center;
  font-family: 'Playfair Display', serif;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormGroup = styled.div`
  position: relative;
  
  .label {
    display: block;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
  }
  
  .required {
    color: #ef4444;
  }
  
  .input {
    width: 100%;
    border: none;
    border-bottom: 2px solid #d1d5db;
    padding: 0.75rem 0;
    font-size: 1rem;
    background: transparent;
    outline: none;
    transition: border-color 0.3s ease;
    
    &:focus {
      border-bottom-color: #253C8C;
    }
    
    &::placeholder {
      color: #9ca3af;
    }
  }
  
  .input-icon {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    background: #253C8C;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .icon {
      color: white;
      font-size: 0.8rem;
    }
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #253C8C 0%, #1e40af 100%);
  border: 2px solid #fbbf24;
  color: white;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%);
    border-color: #f59e0b;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 600;
  font-size: 1.1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContactPage: React.FC<ContactPageProps> = ({
  onHomeClick,
  onBooksClick,
  onVideoLearningClick,
  onBlogsClick,
  onFaqClick,
  onFeedbackClick,
  onLoginClick,
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await contactService.submitContactForm(formData);
      
      if (response.success) {
        setShowSuccessMessage(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 5000);
      } else {
        alert('Failed to submit your message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      alert('Failed to submit your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactContainer>
      <Header
        onHomeClick={onHomeClick}
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onLoginClick={onLoginClick}
        onAdminClick={onAdminClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        onLogout={onLogout}
      />
      
      <ContactContent>
        <ContactInfoSection>
          <ContactInfoItem>
            <div className="icon-container">
              <FiMapPin className="icon" />
            </div>
            <div className="content">
              <div className="label">location:</div>
              <div className="value">Aldie, Virginia, U.S.A</div>
            </div>
          </ContactInfoItem>
          
          <ContactInfoItem>
            <div className="icon-container">
              <FiMail className="icon" />
            </div>
            <div className="content">
              <div className="label">Email us on:</div>
              <div className="value">Mathknots.help@gmail.com</div>
            </div>
          </ContactInfoItem>
          
          <FollowUsSection>
            <div className="title">FOLLOW US</div>
            <div className="social-icons">
              <div className="social-icon">
                <FiFacebook className="icon" />
              </div>
              <div className="social-icon">
                <FiTwitter className="icon" />
              </div>
              <div className="social-icon">
                <span className="icon" style={{ fontFamily: 'Arial', fontWeight: 'bold' }}>P</span>
              </div>
            </div>
          </FollowUsSection>
        </ContactInfoSection>
        
        <ContactFormSection>
          <FormTitle>DROP YOUR MESSAGE</FormTitle>
          {showSuccessMessage && (
            <SuccessMessage>
              ✅ Message sent successfully! We will get back to you soon.
            </SuccessMessage>
          )}
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <label className="label">
                Name<span className="required">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="input"
                placeholder="Enter your full name"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <label className="label">
                Email Address<span className="required">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="input"
                placeholder="Enter your email address"
                required
              />
              <div className="input-icon">
                <FiMail className="icon" />
              </div>
            </FormGroup>
            
            <FormGroup>
              <label className="label">
                Phone Number<span className="required">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="input"
                placeholder="Enter your phone number"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <label className="label">
                Your Message<span className="required">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="input"
                placeholder="Enter your message"
                rows={4}
                required
                style={{ resize: 'vertical', minHeight: '100px' }}
              />
            </FormGroup>
            
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </SubmitButton>
          </Form>
        </ContactFormSection>
      </ContactContent>
      
      <Footer
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onFaqClick={onFaqClick}
        onFeedbackClick={onFeedbackClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
      />
    </ContactContainer>
  );
};

export default ContactPage;
